import { guestUpload, albumSelection, userFolders, blacklistReview, sharedLinks, userRatings, quoteForm,
         dashboard, activity, photos, registerSelfie, past_face_searches } from "../../context/workConstants";

/**
 * Whether the current viewer is allowed to edit the draft album (add/remove
 * photos, lock the album). True for the booking owner, super admins and album
 * collaborators; false for deliverable admins. The backend exposes this on the
 * draft-album / booking-detail responses via `can_edit_draft_album`.
 *
 * This is a UX gate only — the backend enforces the role server-side. We treat
 * a missing flag as allowed so older responses (or non draft-album contexts)
 * keep their existing behaviour; only an explicit `false` hides the controls.
 */
export function get_can_edit_draft_album (collection, data) {
    const flag = collection?.can_edit_draft_album ?? data?.can_edit_draft_album;
    return flag !== false;
}

export function get_global_can_add_to (data) {
    if (data?.photo_data) {
        const result = data.photo_data.reduce((acc, item) => acc || item.can_add_to, false);
        return result;
    }
    return false;
}

export function get_global_is_album_required (data) {
    if (data?.photo_data) {
        const result = data.photo_data.reduce((acc, item) => acc || item.is_album_required, false);
        return result;
    }
    return false;
}

export function get_global_can_make_private (data) {
    if(data?.photo_data){
        const result = data.photo_data.reduce((acc, item) => acc || item.can_make_private, false);
        return result;
    }
    return false;
}

export function get_global_is_downloadable (data) {
    if(data?.photo_data){
        const result = data.photo_data.reduce((acc, item) => acc || item.is_downloadable, false);
        return result;
    }
    return false;
}

export function get_can_add_to (photoId, data) {
    if(data?.photo_data){
        for (const item of data?.photo_data) {
            if (item.data.some(photo => photo.photo_id === photoId)) {
                return item.can_add_to;
            }
        }
    }
}

export function get_is_album_required (photoId, data) {
    if(data?.photo_data){
        for (const item of data?.photo_data) {
            if (item.data.some(photo => photo.photo_id === photoId)) {
                return item.is_album_required;
            }
        }
    }
}

export function get_can_make_private (photoId, data) {
    if(data?.photo_data){
        for (const item of data?.photo_data) {
            if (item.data.some(photo => photo.photo_id === photoId)) {
                return item.can_make_private;
            }
        }
    }
}

export function get_is_downloadable (photoId, data) {
    if(data?.photo_data){
        for (const item of data?.photo_data) {
            if (item.data.some(photo => photo.photo_id === photoId)) {
                return item.is_downloadable;
            }
        }
    }
}

export function get_can_flag_pics(data){
    if(data?.photo_data){
        for (const item of data?.photo_data) {
            return item?.can_flag_pics
        }
        return false;
    }
}

export function get_can_delete (photoId, data) {
    if(data?.photo_data) {
        const photo = data?.photo_data
            .flatMap(e => e.data) 
            .find(ele => ele.photo_id === photoId);
        if(photo){
            return photo.can_delete;
        }
    }
}

export function get_selected_workId (data, photoId) {
    const res = Object.keys(data).find((key) => 
        data[key].some(obj => obj.photo_id === photoId)
    );
    return Number(res);
}

export function get_user_liked_photos (data, photoId) {
    if (!data) return false;

    for (const key of Object.keys(data)) {
        for (const e of Object.keys(data[key])) {
            if (e === photoId) {
                return data[key][e].user_liked;
            }
        }
    }

    return false;
}

export function isCurrentPhotoFlagged(flaggedPhotoIds, photoId) {
    for (const workId in flaggedPhotoIds) {
        if (flaggedPhotoIds[workId].includes(photoId)) {
        return true;
        }
    }
    return false;
}

export function getApiMethod (selectionType, tab) {
    const albumSelection = 'albumselection'
    const deleteUploads = 'delete_guest_gallery'
    const likePhotos = 'like_photos'
    const userFolders = 'userfolders'
    const viewPhotos = 'view_photos'
    const flagPhotos = 'flag_photos'
    return ((tab === albumSelection || tab === userFolders) && selectionType === deleteUploads) || 
           selectionType === likePhotos || 
           selectionType === viewPhotos || selectionType === flagPhotos
           ? "PUT" 
           : "POST";
};

export function sort_data_by_is_downloadable (data, collection, collectionFormattedData) {
    if (!data) return [];
    // built once instead of rescanning every photo inside the comparator, which made this O(n^2 log n)
    const workIdByPhoto = new Map();
    Object.keys(collectionFormattedData || {}).forEach((key) => {
        (collectionFormattedData[key] || []).forEach((obj) => {
            if (!workIdByPhoto.has(obj.photo_id)) workIdByPhoto.set(obj.photo_id, Number(key));
        });
    });
    const downloadableByWork = new Map();
    (collection?.photo_data || []).forEach((obj) => {
        if (!downloadableByWork.has(obj.work_id)) downloadableByWork.set(obj.work_id, obj.is_downloadable);
    });
    const isDownloadable = (item) => downloadableByWork.get(workIdByPhoto.get(item?.photo_id));
    return [...data].sort((a, b) => {
        const a_downloadable = isDownloadable(a);
        const b_downloadable = isDownloadable(b);
        if (a_downloadable !== b_downloadable) {
            return b_downloadable - a_downloadable;
        }
        return b.epoch_time - a.epoch_time;
    });
}

export function get_liked_data_is_downloadable (collection, collectionFormattedData, photo_obj) {
    // find() returns undefined when the photo's work is not in this collection
    return collection?.photo_data.find((obj)=>obj.work_id===get_selected_workId(collectionFormattedData, photo_obj?.photo_id))?.is_downloadable
}

export function getCurrentPage (CONST, tab) {
    let currentPage;
    switch (tab) {
        case guestUpload:
            currentPage = CONST?.GUEST_UPLOADS;
            break;
        case albumSelection:
            currentPage = CONST?.ALBUM_SELECTION;
            break;
        case userFolders:
            currentPage = CONST?.USER_FOLDERS;
            break;
        case blacklistReview:
            currentPage = CONST?.BLACKLIST_REVIEW;
            break;
        case quoteForm:
            currentPage = CONST?.QUOTE_FORM;
            break;
        case sharedLinks:
            currentPage = CONST?.SHARED_LINKS;
            break;
        case userRatings:
            currentPage = CONST?.USER_RATINGS;
            break;
        case dashboard:
            currentPage = CONST?.DASHBOARD;
            break;
        case activity:
            currentPage = CONST?.ACTIVITY;
            break;
        case registerSelfie:
            currentPage = CONST?.REGISTER_SELFIE;
            break;
        default:
            currentPage = CONST?.WORK_DETAILS;
    }
    return currentPage;
}

export function getSelectedData (data, tab) {
    switch (tab) {
        case photos:
            return data?.data['photo-collection'];
        case guestUpload:
            return data?.data['guest-gallery'];
        case blacklistReview:
            return data?.data['blacklist-review'];
        case albumSelection:
            return data?.data['draft-album'];
        case userFolders:
            return data?.data['folders'];
        default:
            return [];
    }
}

export function isValidId(id) {
    return id && id !== 'undefined' && id !== 'null';
}

// True when the payload offers an HD rendition; guest uploads report only ['guest_photos'].
export function has_hd_quality (source) {
    const q = source?.qualities;
    return !Array.isArray(q) || q.includes('hd');
}

// The tab a face search was started from; only nav_history survives the reload into the results.
export function getFaceSearchReturnNav (storedHistory, homeNavType) {
    const entry = (storedHistory || []).find((e) => {
        if (!e?.nav || e.navtype === homeNavType) return false;
        return new URLSearchParams(e.nav.split('?')[1] || '').get('tab') !== past_face_searches;
    });
    return entry?.nav;
}

// On face-search results the home icon returns to the originating tab, not out to the dashboard.
export function getHeaderBackNav (CONST, tab, redirectUrl) {
    if (tab == past_face_searches) {
        const origin = getFaceSearchReturnNav(redirectUrl, CONST?.HOME_URL);
        if (origin) return { nav: origin };
    }
    return redirectUrl?.find(e => e.navtype === CONST?.HOME_URL)
        || redirectUrl?.find(e => e.navtype === CONST?.WORK_DETAILS);
}